/**
 * Team API — Phase 10A.6.
 * Public team page + admin CRUD.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@core/api/client';

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  tagline?: string;
  content?: string;
  icon?: string;
  image?: string;
  order: number;
  hidden: boolean;
  phone?: string;
  email?: string;
  instagram?: string;
  experience?: string;
  is_test: boolean;
}

export interface TeamMemberCreate {
  name: string;
  role: string;
  tagline?: string;
  content?: string;
  icon?: string;
  image?: string;
  order?: number;
  hidden?: boolean;
  phone?: string;
  email?: string;
  instagram?: string;
  experience?: string;
  is_test?: boolean;
}

export interface TeamMemberUpdate {
  name?: string;
  role?: string;
  tagline?: string;
  content?: string;
  icon?: string;
  image?: string;
  order?: number;
  hidden?: boolean;
  phone?: string;
  email?: string;
  instagram?: string;
  experience?: string;
}

export const teamKeys = {
  instructors: () => ['team', 'instructors'] as const,
  instructorsAll: () => ['team', 'instructors', 'all'] as const,
  management: () => ['team', 'management'] as const,
  managementAll: () => ['team', 'management', 'all'] as const,
};

export function useInstructors() {
  return useQuery({
    queryKey: teamKeys.instructors(),
    queryFn: () => api.get<TeamMember[]>('/v1/team/instructors'),
    staleTime: 5 * 60_000,
  });
}

export function useAllInstructors() {
  return useQuery({
    queryKey: teamKeys.instructorsAll(),
    queryFn: () => api.get<TeamMember[]>('/v1/team/instructors/all'),
    staleTime: 5 * 60_000,
  });
}

export function useManagement() {
  return useQuery({
    queryKey: teamKeys.management(),
    queryFn: () => api.get<TeamMember[]>('/v1/team/management'),
    staleTime: 5 * 60_000,
  });
}

export function useAllManagement() {
  return useQuery({
    queryKey: teamKeys.managementAll(),
    queryFn: () => api.get<TeamMember[]>('/v1/team/management/all'),
    staleTime: 5 * 60_000,
  });
}

export function useCreateTeamMember(collection: 'instructors' | 'management') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TeamMemberCreate) =>
      api.post<TeamMember>(`/v1/team/${collection}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team', collection] });
    },
  });
}

export function useUpdateTeamMember(collection: 'instructors' | 'management') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TeamMemberUpdate }) =>
      api.patch<TeamMember>(`/v1/team/${collection}/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team', collection] });
    },
  });
}

export function useDeleteTeamMember(collection: 'instructors' | 'management') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/v1/team/${collection}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team', collection] });
    },
  });
}
